import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button, Card, Chip, ConfirmDeleteModal, EmptyState, Modal, Skeleton } from '../ui'
import type { ChipProps } from '../ui'
import { api, ApiError } from '../../api/client'
import { useSystemCapabilities, useSystemIntegrations } from '../../api/hooks'
import type {
  AccessSystem,
  IntegrationFlags,
  IntegrationType,
  SystemIntegration,
  SystemIntegrationUpdateBody,
} from '../../api/types'
import { useAuthStore } from '../../stores/auth'
import { relativeTime } from '../../lib/format'

const ALL_TYPES: IntegrationType[] = ['web', 'flexc']

const typeLabels: Record<IntegrationType, string> = {
  web: 'Web (HTTP)',
  flexc: 'FlexC',
}

/** Health colors follow the console's status semantics (Chip tones). */
const healthTones: Record<string, ChipProps['tone']> = {
  online: 'ok',
  offline: 'danger',
  degraded: 'warn',
  unknown: 'muted',
}

const flagMeta: { key: keyof IntegrationFlags; label: string; hint: string }[] = [
  { key: 'events', label: 'Events', hint: 'Receive events over this integration. On by default.' },
  { key: 'commands', label: 'Commands', hint: 'Send commands over this integration. On by default.' },
  {
    key: 'virtual_keypad',
    label: 'Virtual keypad',
    hint: 'Keypad control is least-privilege — off until explicitly allowed.',
  },
  {
    key: 'change_user_pins',
    label: 'Change user PINs',
    hint: 'Writing user PINs is least-privilege — off until explicitly allowed.',
  },
]

function errDetail(e: unknown): string {
  if (e instanceof ApiError) return e.detail
  if (e instanceof Error) return e.message
  return 'Request failed'
}

/**
 * One System's SystemIntegration set (one per technology: web/flexc)
 * plus the resolved capability routing table. Every mutation is the
 * full-replace integration PUT; `type` is immutable (re-create to change).
 */
export function IntegrationsCard({ system }: { system: AccessSystem }) {
  const scope = useAuthStore((s) => s.customer?.unique_id ?? 'none')
  const isAdmin = useAuthStore((s) => s.customer?.role === 'admin')
  const queryClient = useQueryClient()

  const integrations = useSystemIntegrations(system.unique_id)
  const routing = useSystemCapabilities(system.unique_id)

  const [addOpen, setAddOpen] = useState(false)
  const [addType, setAddType] = useState<IntegrationType | ''>('')
  const [confirmDelete, setConfirmDelete] = useState<SystemIntegration | null>(null)

  const invalidate = () => queryClient.invalidateQueries({ queryKey: [scope] })

  const update = useMutation({
    mutationFn: ({ iid, body }: { iid: string; body: SystemIntegrationUpdateBody }) =>
      api.updateSystemIntegration(system.unique_id, iid, body),
    onSuccess: invalidate,
  })
  const create = useMutation({
    mutationFn: (type: IntegrationType) => api.createSystemIntegration(system.unique_id, { type }),
    onSuccess: () => {
      invalidate()
      setAddOpen(false)
      setAddType('')
    },
  })
  const remove = useMutation({
    mutationFn: (iid: string) => api.deleteSystemIntegration(system.unique_id, iid),
    onSuccess: () => {
      invalidate()
      setConfirmDelete(null)
    },
  })

  /** Full-replace PUT with the row's current values plus the patch. */
  function saveIntegration(integration: SystemIntegration, patch: Partial<SystemIntegrationUpdateBody>) {
    update.mutate({
      iid: integration.unique_id,
      body: {
        enabled: integration.enabled,
        flags: integration.flags,
        config: integration.config,
        ...patch,
      },
    })
  }

  const present = new Set((integrations.data ?? []).map((i) => i.type))
  // flexc is acre-only (the server 422s anything else — it'd
  // just sit inert on any other provider's capability matrix); types
  // already present would 409, so they are excluded from the picker.
  const ACRE_ONLY_TYPES: IntegrationType[] = ['flexc']
  const addable = ALL_TYPES.filter(
    (t) => !present.has(t) && (!ACRE_ONLY_TYPES.includes(t) || system.provider === 'acre'),
  )

  const actionError = update.error ?? create.error

  return (
    <Card
      title="Integrations"
      action={
        isAdmin && addable.length > 0 ? (
          <Button variant="ghost" size="sm" onClick={() => setAddOpen(true)}>
            + Add integration
          </Button>
        ) : undefined
      }
    >
      {actionError && (
        <p className="mb-3 rounded-sm border border-ostrel-danger/40 bg-ostrel-danger/10 px-3 py-2 text-xs text-ostrel-danger">
          {errDetail(actionError)}
        </p>
      )}

      <p className="mb-3 text-[11px] leading-relaxed text-ostrel-ink-muted">
        Integrations control how capabilities are routed. Disabling an integration stops events/commands
        over that path, but the connector&apos;s own sync tasks keep running while the system is enabled.
        To stop all vendor traffic, disable the system itself.
      </p>

      {integrations.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-14" />
          <Skeleton className="h-14" />
        </div>
      ) : integrations.isError ? (
        <EmptyState
          title="Could not load integrations"
          hint={errDetail(integrations.error)}
          action={
            <Button variant="ghost" size="sm" onClick={() => integrations.refetch()}>
              Retry
            </Button>
          }
        />
      ) : (integrations.data?.length ?? 0) === 0 ? (
        <p className="text-xs text-ostrel-ink-muted">
          No integrations yet. An integration is one technology the system can be reached through —
          the migration backfilled a <span className="font-mono">web</span> row for older systems.
        </p>
      ) : (
        <ul className="flex flex-col gap-3">
          {integrations.data?.map((integration) => (
            <li
              key={integration.unique_id}
              data-testid={`integration-${integration.type}`}
              className="rounded-sm border border-ostrel-line bg-ostrel-card px-3.5 py-3"
            >
              <div className="flex flex-wrap items-center gap-2">
                <Chip tone="gold" label={typeLabels[integration.type] ?? integration.type} />
                <Chip tone={healthTones[integration.health] ?? 'muted'} label={integration.health} />
                <span className="tnum text-[11px] text-ostrel-ink-muted">
                  {integration.health_at ? relativeTime(integration.health_at) : 'never seen'}
                </span>
                <span className="tnum font-mono text-[11px] text-ostrel-ink-muted">
                  {integration.unique_id}
                </span>
                <span className="ml-auto flex items-center gap-3">
                  <label className="flex cursor-pointer items-center gap-2 text-xs text-ostrel-ink">
                    <input
                      type="checkbox"
                      checked={integration.enabled}
                      disabled={!isAdmin || update.isPending}
                      onChange={(e) => saveIntegration(integration, { enabled: e.target.checked })}
                      className="h-3.5 w-3.5 accent-ostrel-gold"
                    />
                    Enabled
                  </label>
                  {isAdmin && (
                    <Button variant="danger" size="sm" onClick={() => setConfirmDelete(integration)}>
                      Delete
                    </Button>
                  )}
                </span>
              </div>
              <div className="mt-3 grid gap-x-6 gap-y-2 border-t border-ostrel-line pt-3 sm:grid-cols-2">
                {/* The API exposes only provider-declared flags (integration_flags
                    filters IntegrationOut) — render exactly those, never a static
                    superset (an acre web row has no keypad/PIN meaning). */}
                {flagMeta
                  .filter((flag) => flag.key in integration.flags)
                  .map((flag) => (
                  <label
                    key={flag.key}
                    className="flex cursor-pointer items-start gap-2.5"
                    title={isAdmin ? flag.hint : `${flag.hint} (requires the admin role)`}
                  >
                    <input
                      type="checkbox"
                      checked={integration.flags[flag.key] ?? false}
                      disabled={!isAdmin || update.isPending}
                      onChange={(e) =>
                        saveIntegration(integration, {
                          flags: { ...integration.flags, [flag.key]: e.target.checked },
                        })
                      }
                      className="mt-0.5 h-3.5 w-3.5 accent-ostrel-gold"
                    />
                    <span>
                      <span className="block text-xs font-medium text-ostrel-ink">{flag.label}</span>
                      <span className="block text-[11px] leading-snug text-ostrel-ink-muted">
                        {flag.hint}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-4 border-t border-ostrel-line pt-3">
        <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
          Routing
        </p>
        {routing.isPending ? (
          <Skeleton className="h-9" />
        ) : routing.isError ? (
          <p className="text-xs text-ostrel-danger">{errDetail(routing.error)}</p>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {Object.entries(routing.data ?? {}).map(([capability, resolved]) => (
              <li key={capability} className="flex flex-wrap items-baseline gap-x-2 text-xs">
                <span className="font-mono font-medium text-ostrel-ink">{capability}</span>
                <span className="text-ostrel-ink">
                  {resolved.integration ? (
                    <>
                      <Chip tone="muted" label={resolved.type ?? '?'} />{' '}
                      <span className="tnum font-mono text-[11px]">{resolved.integration}</span>
                    </>
                  ) : (
                    <span className="text-ostrel-ink-muted">— unrouted</span>
                  )}
                </span>
                <span className="text-[11px] text-ostrel-ink-muted">{resolved.why}</span>
              </li>
            ))}
            {Object.keys(routing.data ?? {}).length === 0 && (
              <li className="text-xs text-ostrel-ink-muted">No capabilities for this provider.</li>
            )}
          </ul>
        )}
      </div>

      <Modal open={addOpen} title="Add integration" onClose={() => setAddOpen(false)}>
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault()
            if (addType && !create.isPending) create.mutate(addType)
          }}
        >
          <label className="flex flex-col gap-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
              Type
            </span>
            <select
              value={addType}
              onChange={(e) => setAddType(e.target.value as IntegrationType)}
              className="rounded-sm border border-ostrel-line bg-ostrel-card px-3 py-2 text-sm text-ostrel-ink focus:border-ostrel-gold focus:outline-none focus:ring-1 focus:ring-ostrel-gold"
            >
              <option value="">— pick a type</option>
              {addable.map((t) => (
                <option key={t} value={t}>
                  {typeLabels[t]}
                </option>
              ))}
            </select>
          </label>
          <p className="text-[11px] leading-relaxed text-ostrel-ink-muted">
            One integration per type. Flags start at the least-privilege defaults (events/commands
            on, keypad/PIN writes off); adjust them after creating.
          </p>
          {create.error && (
            <p className="rounded-sm border border-ostrel-danger/40 bg-ostrel-danger/10 px-3 py-2 text-xs text-ostrel-danger">
              {errDetail(create.error)}
            </p>
          )}
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setAddOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="navy" size="sm" disabled={!addType || create.isPending}>
              {create.isPending ? 'Adding…' : 'Add integration'}
            </Button>
          </div>
        </form>
      </Modal>

      <ConfirmDeleteModal
        open={confirmDelete !== null}
        title="Delete integration"
        objectName={confirmDelete ? typeLabels[confirmDelete.type] : ''}
        expected={confirmDelete?.unique_id ?? ''}
        pending={remove.isPending}
        error={remove.error ? errDetail(remove.error) : null}
        confirmLabel="Delete integration"
        onConfirm={() => confirmDelete && remove.mutate(confirmDelete.unique_id)}
        onClose={() => setConfirmDelete(null)}
      >
        <p>
          Delete the <strong>{confirmDelete ? typeLabels[confirmDelete.type] : ''}</strong>{' '}
          integration ({confirmDelete?.unique_id})? Capabilities routed to it fall back to the next
          preferred integration, or stop routing.
        </p>
      </ConfirmDeleteModal>
    </Card>
  )
}
